import axios from 'axios';
import { clearAuthMarkerCookie, redirectToLogin } from '@/utils/authSession';
import { LANGUAGE_STORAGE_KEY } from '@/i18n';

/**
 * 开发环境必须走相对路径，经 CRA setupProxy 同源转发。
 * 若设成 http://localhost.admin-letsfly 会变跨域，XSRF Cookie 读不到 → 419。
 */
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

const apiClient = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

apiClient.interceptors.request.use((config) => {
  // FormData（multipart 上传）时移除全局 Content-Type，交由浏览器自动带 boundary，
  // 否则 axios 保留显式 application/json，后端会收不到 file 字段
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  // 后端 SetLocale 中间件据此解析语言（Phase 3 落地后生效）
  try {
    const lang = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (lang) {
      config.headers['X-App-Locale'] = lang;
    }
  } catch {
    // 存储不可用时忽略
  }

  if (config.method && ['post', 'put', 'patch', 'delete'].includes(config.method)) {
    const token = document.cookie
      .split('; ')
      .find((row) => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1];
    if (token) {
      config.headers['X-XSRF-TOKEN'] = decodeURIComponent(token);
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      const path = typeof window !== 'undefined' ? window.location.pathname : '';

      // 后端 ApiResponse.message（status_codes 双语）抽取到 err.message，供 UI 直接展示
      if (data && typeof data.message === 'string' && data.message) {
        error.message = data.message;
      }

      if (status === 401) {
        clearAuthMarkerCookie();
        redirectToLogin();
      } else if (status === 419 && typeof window !== 'undefined') {
        // 登录回调页禁止 reload，否则会 ticket 未消费就反复 419 死循环
        if (!path.startsWith('/auth/callback')) {
          window.location.reload();
        }
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
